import { Button, Heading, Stack, Tag, Text, Tooltip } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { RequestDraftStatus } from '@prisma/client';
import { HiArrowTopRightOnSquare } from 'react-icons/hi2';
import { Link } from 'react-router-dom';

import { useCurrentDraft } from '../../hooks/useCurrentDraft';
import { CopyableText } from '../CopyableText';
import { statusColor } from './types';

/**
 * Deep link to the original email in the ops Gmail inbox, by RFC822 Message-ID (stored with angle brackets; Gmail's
 * search wants them stripped). Opens in the signed-in mailbox account.
 */
const gmailUrl = (messageId?: string | null) =>
  messageId
    ? `https://mail.google.com/mail/u/0/#search/rfc822msgid:${encodeURIComponent(messageId.replace(/^<|>$/g, ''))}`
    : null;

/** Title row: the matched request's R-number (copyable), status tag, and the two navigation actions. */
export const DraftHeader = () => {
  const { draft, isPending, isBookingConfirmation } = useCurrentDraft();

  // Pending drafts with a dedicated view deep-link to their panel on the
  // request page (?draft=id). The param is a stable contract between the inbox
  // and the request page — keep it when new routes get a view.
  const requestPath = draft?.request_id
    ? `/requests/${draft.request_id}/airline${isBookingConfirmation && isPending ? `?draft=${draft.id}` : ''}`
    : null;

  return (
    <Stack px="5" py="3" borderBottomWidth="1px" spacing="1">
      <Stack direction="row" align="center" spacing="3" wrap="wrap">
        <Show if={!!draft?.request_id}>
          <CopyableText fontSize="xl" fontWeight="600" userSelect="all" value={`R${draft?.request_id}`} />
        </Show>
        <Show if={!draft?.request_id}>
          <Heading variant="h3">Draft #{draft?.id ?? ''}</Heading>
        </Show>
        <Show if={!!draft?.status}>
          <Tag colorScheme={draft?.status ? statusColor[draft.status] : undefined}>{draft?.status}</Tag>
        </Show>
        <Show if={!!draft?.is_low_confidence}>
          <Tooltip label="Classified below the confidence threshold — treat the routing as a guess and read the source email before acting on it.">
            <Tag colorScheme="orange">Low Confidence</Tag>
          </Tooltip>
        </Show>
        <Show if={!!gmailUrl(draft?.message_id)}>
          <Button
            as="a"
            href={gmailUrl(draft?.message_id) as string}
            target="_blank"
            rel="noopener noreferrer"
            size="xs"
            variant="outline"
            rightIcon={<HiArrowTopRightOnSquare />}
            ml="auto"
          >
            Open email
          </Button>
        </Show>
        <Show if={!!requestPath}>
          <Button as={Link} to={requestPath ?? ''} size="xs" variant="outline">
            Open request →
          </Button>
        </Show>
      </Stack>
      <Show if={draft?.status === RequestDraftStatus.Discarded && !!draft?.discard_reason}>
        <Text fontSize="sm" color="gray.600">
          Discarded: {draft?.discard_reason}
        </Text>
      </Show>
    </Stack>
  );
};
